import moment from "moment";
import { ReadAllResponse, uuid } from "@/interfaces";
import { store } from "@/lib/store.ts";
import {
  appointment_status,
  individual_payment_status,
  subs_payment_status,
  AppointmentMetaData,
} from "@/models/appointment.model.ts";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import type { BillingException, NoteAudience } from "@shyne/shared/api";
import { toAppointmentLog, type AppointmentLogJoin } from "@/repositories/appointment-history.ts";
import { CatalogRepository } from "@/repositories/catalog.repository";
import {
  isRowStatus,
  readAppointmentPage,
  readEveryAppointment,
  toApiFilters,
  toApiSortKey,
  toAppointmentJoin,
  toFilterOptions,
  toRowStatusCounts,
  type AppointmentRowFilters,
  type RowStatusCounts,
} from "@/repositories/appointment-records.ts";

export interface AppointmentJoin {
  id: uuid;
  property: {
    id: uuid;
    name: string;
    unit_number: string;
    beds: number;
    baths: number;
    image?: string;
    address?: string;
  };
  vendor: {
    id: uuid;
    name: string;
  };
  service: {
    id: uuid;
    name: string;
    parent_name: string;
  };
  assigned_user: {
    id: uuid | null;
    name: string | null;
  };
  assigned_organization?: {
    id: uuid | null;
    display_name: string | null;
  } | null;
  created_at: string;
  date: string;
  time?: string;
  status: appointment_status;
  work_order_notes?: string;
  final_price?: number | null;
  individual_payment?: number | null;
  individual_payment_status?: individual_payment_status | null;
  subs_payment?: number | null;
  subs_payment_status?: subs_payment_status | null;
  cancelled_by_user?: { id: uuid; name: string } | null;
  meta_data?: AppointmentMetaData;
  billing_exception?: BillingException | null;
}

export interface AppointmentFilterOptions {
  serviceParents: string[];
  vendorNames: string[];
  assigneeNames: string[];
}

export interface VendorOption {
  id: uuid;
  name: string;
}

export interface ServiceOption {
  id: uuid;
  name: string;
}

export type { AppointmentLogJoin } from "@/repositories/appointment-history.ts";

export const activeOrganization = (): uuid | null => store.getState().organization.activeOrgId ?? null;

type ListPage = { page: number; pageSize: number; sortKey: string; sortOrder: "asc" | "desc" };

async function listAppointmentJoins(
  filters: AppointmentRowFilters,
  actingOrganization: uuid | null,
  { page, pageSize, sortKey, sortOrder }: ListPage
): Promise<ReadAllResponse<AppointmentJoin>> {
  if (filters.status && !isRowStatus(filters.status)) return { data: [], count: 0 };

  const query = { ...toApiFilters(filters, actingOrganization), sort: toApiSortKey(sortKey), order: sortOrder };
  const { items, total } = await readAppointmentPage(api.appointments.list, query, page, pageSize);
  return { data: items.map(toAppointmentJoin), count: total };
}

type DayRange = { dateFrom?: string; dateTo?: string };

const localDay = (date: Date) => moment(date).format("YYYY-MM-DD");

const daysBetween = (startDate?: Date, endDate?: Date): DayRange => ({
  dateFrom: startDate && localDay(startDate),
  dateTo: endDate && localDay(endDate),
});

const nextDays = (days: number) => daysBetween(new Date(), moment().add(days, "days").toDate());

async function listEveryAppointmentJoin(range: DayRange): Promise<AppointmentJoin[]> {
  await requirePermission("appointments.view");

  const query = { ...toApiFilters(range, activeOrganization()), sort: "date" as const, order: "asc" as const };
  return (await readEveryAppointment(api.appointments.list, query)).map(toAppointmentJoin);
}

async function countAppointments(range: DayRange) {
  await requirePermission("appointments.view");

  return api.appointments.counts(toApiFilters(range, activeOrganization()));
}

async function countOrganizationRowStatuses(range: DayRange): Promise<RowStatusCounts> {
  await requirePermission("appointments.view");

  const organization = activeOrganization();
  if (!organization) return toRowStatusCounts({});

  return toRowStatusCounts(await api.appointments.counts(toApiFilters(range, organization)));
}

async function listOrganizationFilterOptions(): Promise<{ serviceParents: string[]; assigneeNames: string[] }> {
  await requirePermission("appointments.view");

  const organization = activeOrganization();
  if (!organization) return { serviceParents: [], assigneeNames: [] };

  const { serviceParents, assigneeNames } = toFilterOptions(await api.appointments.filterOptions(toApiFilters({}, organization)));
  return { serviceParents, assigneeNames };
}

export const AppointmentsRepository = {
  async getById(id: uuid): Promise<AppointmentJoin> {
    await requirePermission("appointments.view");
    return toAppointmentJoin(await api.appointments.get(id));
  },

  async list(
    page = 1,
    pageSize = 10,
    filters: {
      status?: appointment_status;
      serviceParent?: string;
      vendor?: string;
      assignee?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<AppointmentJoin>> {
    await requirePermission("appointments.view");

    const { vendor, assignee, ...rest } = filters;
    return listAppointmentJoins({ ...rest, vendorName: vendor, assigneeUser: assignee }, activeOrganization(), { page, pageSize, sortKey, sortOrder });
  },

  async listForProperty(
    propertyId: uuid,
    page = 1,
    pageSize = 10,
    filters: {
      status?: appointment_status;
      serviceParent?: string;
      assignee?: string;
      search?: string;
    },
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<AppointmentJoin>> {
    await requirePermission("appointments.view");

    const { assignee, ...rest } = filters;
    return listAppointmentJoins({ ...rest, property: propertyId, assigneeUser: assignee }, activeOrganization(), { page, pageSize, sortKey, sortOrder });
  },

  async listFilterOptionsForProperty(propertyId: uuid): Promise<AppointmentFilterOptions> {
    await requirePermission("appointments.view");

    return toFilterOptions(await api.appointments.filterOptions(toApiFilters({ property: propertyId }, activeOrganization())));
  },

  async listFilterOptions(): Promise<AppointmentFilterOptions> {
    await requirePermission("appointments.view");

    return toFilterOptions(await api.appointments.filterOptions(toApiFilters({}, activeOrganization())));
  },

  async listParentCategoriesForOrg(): Promise<string[]> {
    await requirePermission("appointments.view");

    if (!activeOrganization()) return [];
    return CatalogRepository.parentNames();
  },

  async listVendorsForParent(parentName: string): Promise<VendorOption[]> {
    await requirePermission("appointments.view");

    if (!activeOrganization()) return [];
    return CatalogRepository.vendorsUnder(parentName);
  },

  async listParentCategoriesForVendor(vendorId: uuid): Promise<string[]> {
    return CatalogRepository.parentNames(vendorId);
  },

  async listChildServices(vendorId: uuid, parentName: string): Promise<ServiceOption[]> {
    await requirePermission("appointments.view");

    return CatalogRepository.servicesUnder(vendorId, parentName);
  },

  async listHistory(id: uuid): Promise<AppointmentLogJoin[]> {
    await requirePermission("appointments.view");
    return (await api.appointments.history(id, { actingOrganization: activeOrganization() ?? undefined })).items.map(toAppointmentLog);
  },

  async addNote(appointmentId: uuid, message: string, audience?: NoteAudience[]): Promise<void> {
    await api.appointments.addNote(appointmentId, { message, actingOrganization: activeOrganization() ?? undefined, ...(audience === undefined ? {} : { audience }) });
  },

  async listForVendor(
    page = 1,
    pageSize = 10,
    filters: {
      status?: appointment_status;
      serviceParent?: string;
      assignee?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<AppointmentJoin>> {
    await requirePermission("appointments.view");

    const vendorOrgId = activeOrganization();
    if (!vendorOrgId) return { data: [], count: 0 };

    return listAppointmentJoins({ ...filters, searchVendor: false }, vendorOrgId, { page, pageSize, sortKey, sortOrder });
  },

  countsForVendor: countOrganizationRowStatuses,

  listFilterOptionsForVendor: listOrganizationFilterOptions,

  async listForIndividual(
    page = 1,
    pageSize = 10,
    filters: {
      status?: appointment_status;
      statuses?: readonly appointment_status[];
      serviceParent?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<AppointmentJoin>> {
    await requirePermission("appointments.view");

    return listAppointmentJoins({ ...filters, searchVendor: false }, activeOrganization(), { page, pageSize, sortKey, sortOrder });
  },

  async countsForIndividual(): Promise<RowStatusCounts> {
    return toRowStatusCounts(await countAppointments({}));
  },

  async listFilterOptionsForIndividual(): Promise<{ serviceParents: string[] }> {
    const { serviceParents } = await AppointmentsRepository.listFilterOptions();
    return { serviceParents };
  },

  async listForSubs(
    page = 1,
    pageSize = 10,
    filters: {
      status?: appointment_status;
      serviceParent?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<AppointmentJoin>> {
    await requirePermission("appointments.view");

    const subsOrgId = activeOrganization();
    if (!subsOrgId) return { data: [], count: 0 };

    return listAppointmentJoins(filters, subsOrgId, { page, pageSize, sortKey, sortOrder });
  },

  countsForSubs: countOrganizationRowStatuses,

  listFilterOptionsForSubs: listOrganizationFilterOptions,

  async listUpcoming(days: number): Promise<AppointmentJoin[]> {
    return listEveryAppointmentJoin(nextDays(days));
  },

  async countUpcoming(days: number): Promise<number> {
    return (await countAppointments(nextDays(days))).total;
  },

  async listByDateRange(startDate: Date, endDate: Date): Promise<AppointmentJoin[]> {
    return listEveryAppointmentJoin(daysBetween(startDate, endDate));
  },

  async countWithGobackAt(startDate?: Date, endDate?: Date): Promise<number> {
    return (await countAppointments(daysBetween(startDate, endDate))).withGoBackRequestedAt;
  },
};
